
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { measureAsyncExecutionTime } from '@/utils/performance';
import { reportRouteChange } from '@/utils/web-vitals';
import env from '@/config/env';

/**
 * A hook that tracks route changes and reports performance metrics
 */
export function useRouteChangeTracker() {
  const location = useLocation();
  
  useEffect(() => {
    const currentPath = location.pathname + location.search;
    
    const trackPageview = async () => {
      // Report the route change to our web vitals tracker
      reportRouteChange(currentPath);
      
      // Reset layout shift tracking for new page
      if ('performance' in window) {
        performance.mark(`route-change-${currentPath}`);
      }
      
      // Only send analytics in production
      if (env.isProduction && env.analyticsEnabled) {
        try {
          // This would be your analytics tracking call
          // e.g., gtag('config', GA_ID, { page_path: currentPath });
          console.log(`[Analytics] Pageview: ${currentPath}`);
        } catch (error) {
          console.error('Error sending analytics', error);
        }
      }
    };
    
    // Track the pageview, measuring how long it takes
    measureAsyncExecutionTime(trackPageview, `Route change to ${currentPath}`);
    
    // Opportunity to prefetch likely next routes based on current route
    const prefetchNextLikelyRoutes = () => {
      // Example: if on company page, user might view jobs next
      if (currentPath.includes('/companies/')) {
        // This would use a more sophisticated prefetching system in a real app
        // prefetchRoute('/jobs');
      }
    };
    
    // Schedule prefetch during idle time
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(prefetchNextLikelyRoutes);
    } else {
      setTimeout(prefetchNextLikelyRoutes, 1000);
    }
    
    return () => {
      // Clean up any subscriptions or timers if needed
      if ('performance' in window) {
        performance.mark(`route-exit-${currentPath}`);
        performance.measure(
          `route-duration-${currentPath}`,
          `route-change-${currentPath}`,
          `route-exit-${currentPath}`
        );
      }
    };
  }, [location]);
  
  return null;
}
